/**
 * LeagueSettingsTab — iter87.6. Rivalry-wide settings that DON'T fit
 * anywhere else. After the tile split, invites → RivalryInvitesTab,
 * messages → RivalryMessagesTab, co-admins → RivalryCoAdminsTab. What
 * remains here: spectator mode, admin transfer (still gated off), and
 * any future rivalry-identity controls.
 */
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Users, MessageCircleWarning } from "lucide-react";
import { useState } from "react";
import TransferAdminCard from "@/components/TransferAdminCard";
import ReportIssueModal from "@/components/ReportIssueModal";
import LeagueDangerZoneCard from "@/components/admin/LeagueDangerZoneCard";

export default function LeagueSettingsTab({
    league, leagueId, data, coAdminCtx, isLocked, onChanged,
}) {
    const [reportOpen, setReportOpen] = useState(false);

    const handleToggleSpectator = async () => {
        try {
            const next = !(league?.spectator_enabled || false);
            await api.setSpectatorMode(leagueId, next);
            toast.success(next ? "Spectator mode ON — anyone with the rivalry link can view" : "Spectator mode OFF — players only");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const handleCopySpectatorLink = async () => {
        const url = `${window.location.origin}/league/${leagueId}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Spectator link copied to clipboard");
        } catch {
            toast.message("Spectator link", { description: url });
        }
    };

    return (
        <div className="space-y-5" data-testid="league-settings-tab-card">
            <div className="brutal-card-light p-3 flex items-start gap-3 border-cyber/40" data-testid="league-scope-banner">
                <div className="w-8 h-8 bg-cyber border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <p className="text-xs text-zinc-300 leading-relaxed">
                    <strong className="text-cyber">Rivalry-wide settings</strong> — these persist across every tournament you run in this rivalry.
                    Invites, messages, and co-admins each live in their own tile now; this surface is for identity,
                    spectator mode, and the danger zone.
                </p>
            </div>

            <div className="brutal-card p-5 space-y-3" data-testid="spectator-mode-card">
                <h2 className="font-display font-extrabold text-xl uppercase">Spectator mode</h2>
                <p className="text-xs text-zinc-400 leading-relaxed">
                    When enabled, anyone with the rivalry's URL can view the leaderboard, bracket, and recent matches — no invite code needed. Great for sharing on social or with friends/family who just want to follow along. Drafted teams, knockout picks, and admin tools stay private.
                </p>
                <div className="flex items-center justify-between flex-wrap gap-3 brutal-card-light p-4">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-zinc-500">Currently</p>
                        <p className="font-display font-extrabold text-lg" data-testid="spectator-mode-status">
                            {league?.spectator_enabled ? <span className="text-cyber">ON · public can view</span> : <span className="text-zinc-400">OFF · players only</span>}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {league?.spectator_enabled && (
                            <button
                                data-testid="copy-spectator-link-btn"
                                onClick={handleCopySpectatorLink}
                                className="brutal-btn-ghost text-xs"
                            >
                                <Copy className="w-3.5 h-3.5" /> Copy share link
                            </button>
                        )}
                        <button
                            data-testid="toggle-spectator-mode-btn"
                            onClick={handleToggleSpectator}
                            disabled={isLocked}
                            className={`brutal-btn text-xs ${league?.spectator_enabled ? "bg-blaze text-white" : "bg-cyber text-black"} border-black shadow-brutal disabled:opacity-40`}
                        >
                            {league?.spectator_enabled ? "Turn OFF" : "Turn ON"}
                        </button>
                    </div>
                </div>
            </div>

            {/* iter63z'' — Admin Transfer hidden by user request; will be re-enabled
                alongside the admin-transfer-email + T&C auto-prompt feature (P3). */}
            {false && coAdminCtx?.role !== "co_admin" && (
                <TransferAdminCard
                    league={league}
                    participants={data?.participants || []}
                    leagueId={leagueId}
                    onChanged={onChanged}
                />
            )}

            {/* iter87.105 — Report an Issue. Admin-only per operator spec. */}
            <div className="brutal-card p-5 space-y-3" data-testid="report-issue-card">
                <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                        <MessageCircleWarning className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display font-extrabold text-xl uppercase">Report an Issue</h2>
                        <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                            Bug, confusion, or feature idea — pick a category, check the FAQ, and file if you still need us. Only Rivalry admins see this.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setReportOpen(true)}
                    className="brutal-btn text-sm py-2 px-4 border-volt text-volt w-full"
                    data-testid="report-issue-open-btn"
                >
                    <MessageCircleWarning className="w-4 h-4" /> Report an issue
                </button>
            </div>

            <ReportIssueModal
                open={reportOpen}
                onClose={() => setReportOpen(false)}
                leagueId={leagueId}
            />

            {/* iter87.407 — Rivalry Danger Zone tile: admin's only action
                is Close (reversible via Reopen). Archive is site-admin
                only now (corrected from iter87.115's one-way admin
                archive). Placed last so admins never fat-finger a
                destructive action from a routine settings visit. */}
            <LeagueDangerZoneCard
                league={league}
                leagueId={leagueId}
                onChanged={onChanged}
            />
        </div>
    );
}
